import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RupiahInput from "@/components/RupiahInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Money, ArrowRight, Coins, Warning, Wallet, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import PayoffReceipt from "@/components/PayoffReceipt";
import Combobox from "@/components/Combobox";

const daysBadge = (days) => {
  if (days < 0) return { bg: "#374151", label: `H-${Math.abs(days)}` };
  if (days === 0) return { bg: "#2563EB", label: "Hari Ini" };
  if (days <= 7) return { bg: "#F59E0B", label: `Telat ${days}h` };
  if (days <= 30) return { bg: "#F97316", label: `Telat ${days}h` };
  return { bg: "#E63946", label: `Telat ${days}h` };
};

const monthsBetween = (from, to) => {
  const a = new Date(from + "T00:00:00");
  const b = new Date(to + "T00:00:00");
  let m = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) m -= 0.5; // rough half-month
  return Math.max(0, m);
};

/**
 * Compute payoff summary for a credit sale.
 * Bunga flat: total_interest = P × r × T/12. Jika dilunasi bulan ke-k dari T,
 * bunga efektif = P × r × k/12. Discount = original_interest − effective_interest.
 */
function computePayoff(sale) {
  const price = Number(sale.total_price || 0);
  const dp = Number(sale.down_payment || 0);
  const principal = Math.max(price - dp, 0);
  const rate = Number(sale.annual_rate || 0);
  const tenor = Number(sale.tenor_months || 0);
  const insts = sale.installments || [];

  const today = new Date().toISOString().slice(0, 10);
  const elapsed = Math.min(monthsBetween(sale.sale_date, today), tenor);

  const originalInterest = principal * (rate / 100) * (tenor / 12);
  const effectiveInterest = principal * (rate / 100) * (elapsed / 12);
  const totalOriginal = principal + originalInterest;
  const totalEffective = principal + effectiveInterest;
  const totalPaid = insts.reduce((a, i) => a + Number(i.paid_amount || 0), 0);
  const payoffToday = Math.max(totalEffective - totalPaid, 0);
  const savings = Math.max(totalOriginal - totalPaid - payoffToday, 0);

  const now = new Date(); now.setHours(0, 0, 0, 0);
  let overdueAmount = 0, overdueCount = 0, remainingAll = 0;
  for (const inst of insts) {
    if (inst.status === "paid") continue;
    const sisa = Number(inst.amount) - Number(inst.paid_amount || 0);
    remainingAll += sisa;
    const due = new Date(inst.due_date + "T00:00:00");
    if (due < now) { overdueAmount += sisa; overdueCount += 1; }
  }
  return { principal, rate, tenor, elapsed, originalInterest, effectiveInterest, totalOriginal, totalEffective, totalPaid, payoffToday, savings, overdueAmount, overdueCount, remainingAll };
}

export default function Payments() {
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payDestination, setPayDestination] = useState("SM"); // SM = Showroom (default), KP = Kantor Pusat
  const [settings, setSettings] = useState(null);
  const [payoffReceipt, setPayoffReceipt] = useState(null); // { sale, amount, date }
  const [quickSel, setQuickSel] = useState(""); // sale.id selected in quick-pay panel

  const load = () => api.get("/sales").then((r) => setSales(r.data.filter((s) => s.sale_type === "credit")));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    return sales
      .map((s) => ({ sale: s, summary: computePayoff(s) }))
      .filter(({ sale, summary }) => summary.remainingAll > 0.01)
      .filter(({ sale }) => {
        if (!query) return true;
        return (
          (sale.account_number || "").toLowerCase().includes(query) ||
          (sale.customer?.name || "").toLowerCase().includes(query) ||
          (sale.customer?.phone || "").toLowerCase().includes(query) ||
          (sale.car?.brand || "").toLowerCase().includes(query) ||
          (sale.car?.model || "").toLowerCase().includes(query)
        );
      })
      .sort((a, b) => b.summary.overdueAmount - a.summary.overdueAmount);
  }, [sales, q]);

  const openPay = (sale, inst, amount) => {
    setSelected({ sale, inst, isPayoff: !inst, payoffAmount: amount });
    setPayAmount(String(amount || Math.max(0, (inst?.amount || 0) - (inst?.paid_amount || 0)).toFixed(0)));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayOpen(true);
  };

  const openDpPay = (sale, termin) => {
    setSelected({ sale, dpTermin: termin, isDp: true });
    const sisa = Math.max(0, Number(termin.amount) - Number(termin.paid_amount || 0));
    setPayAmount(String(sisa.toFixed(0)));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!selected) return;
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Jumlah bayar harus lebih dari 0"); return; }
    try {
      if (selected.isPayoff) {
        // Single backend call: settle all remaining installments & write off interest discount
        const r = await api.post(`/sales/${selected.sale.id}/payoff`, {
          amount: amt,
          payment_date: payDate,
          cash_destination: payDestination,
        });
        toast.success("Pelunasan berhasil dicatat");
        // Trigger printable payoff receipt using authoritative server total
        setPayoffReceipt({ sale: selected.sale, amount: r.data.amount || amt, date: payDate });
        setTimeout(() => {
          window.print();
          // Unmount the printable receipt shortly after the print dialog opens
          setTimeout(() => setPayoffReceipt(null), 800);
        }, 400);
      } else {
        await api.post(`/sales/${selected.sale.id}/payments`, {
          installment_id: selected.inst.id,
          amount: parseFloat(payAmount),
          payment_date: payDate,
          cash_destination: payDestination,
        });
        toast.success("Pembayaran dicatat");
      }
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencatat");
    }
  };

  return (
    <div>
      <div className="no-print">
      <PageHeader title="Bayar Cicilan" subtitle="Rekening kredit aktif — dengan info tunggakan, sisa piutang, dan pelunasan hari ini" />
      <div className="p-4 md:p-6 lg:p-10">
        {/* Quick Pay Panel — pilih no rekening lewat autocomplete, langsung tampil info + tombol bayar/lunasi */}
        {(() => {
          const openSales = sales
            .map((s) => ({ sale: s, summary: computePayoff(s) }))
            .filter(({ summary }) => summary.remainingAll > 0.01);
          const selectedRow = openSales.find((r) => r.sale.id === quickSel);
          const nextInst = selectedRow ? (selectedRow.sale.installments || [])
            .filter((i) => i.status !== "paid")
            .sort((a, b) => a.sequence - b.sequence)[0] : null;
          const nextSisa = nextInst ? (Number(nextInst.amount) - Number(nextInst.paid_amount || 0)) : 0;

          return (
            <div className="border border-gray-200 bg-white mb-6" data-testid="quick-pay-panel">
              <div className="p-5 border-b border-gray-200 bg-[#0A0A0A] text-white flex items-center gap-2">
                <MagnifyingGlass size={16} weight="bold" />
                <div className="font-display font-bold uppercase tracking-wider text-sm">Cari No. Rekening — Bayar Cepat</div>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Pilih No. Rekening</Label>
                  <div className="mt-2">
                    <Combobox
                      value={quickSel}
                      onChange={setQuickSel}
                      options={openSales.map(({ sale }) => ({
                        value: sale.id,
                        label: `${sale.account_number || "-"} · ${sale.customer?.name || ""} · ${sale.car?.brand || ""} ${sale.car?.model || ""}`,
                        keywords: `${sale.account_number || ""} ${sale.customer?.name || ""} ${sale.customer?.phone || ""} ${sale.car?.brand || ""} ${sale.car?.model || ""} ${sale.car?.plate_number || ""}`,
                      }))}
                      placeholder="Ketik no. rekening / nama / plat untuk mencari..."
                      searchPlaceholder="Ketik no. rekening / nama / plat..."
                      emptyText="Tidak ada rekening kredit aktif yang cocok"
                      testid="quick-pay-combobox"
                    />
                  </div>
                </div>

                {selectedRow && (
                  <>
                    <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-6 gap-0 border border-gray-200 -m-px">
                      <div className="p-4 border border-gray-200">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">No. Rek</div>
                        <div className="font-mono-num font-bold text-lg mt-1" data-testid="quick-info-rek">{selectedRow.sale.account_number || "-"}</div>
                      </div>
                      <div className="p-4 border border-gray-200 md:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Mobil</div>
                        <div className="font-semibold mt-1" data-testid="quick-info-car">
                          {selectedRow.sale.car?.brand} {selectedRow.sale.car?.model}
                        </div>
                        <div className="text-[11px] text-gray-500 font-mono-num">{selectedRow.sale.car?.type || "-"}</div>
                      </div>
                      <div className="p-4 border border-gray-200">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Tahun</div>
                        <div className="font-mono-num font-bold text-lg mt-1" data-testid="quick-info-year">{selectedRow.sale.car?.year || "-"}</div>
                      </div>
                      <div className="p-4 border border-gray-200">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Plat</div>
                        <div className="font-mono-num font-bold text-sm mt-1" data-testid="quick-info-plate">{selectedRow.sale.car?.plate_number || "-"}</div>
                      </div>
                      <div className="p-4 border border-gray-200 bg-blue-50">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Angsuran</div>
                        <div className="font-mono-num font-bold text-lg mt-1 text-[#2563EB]" data-testid="quick-info-inst-no">
                          {nextInst ? `#${nextInst.sequence}` : "-"}
                          <span className="text-xs text-gray-500 font-normal"> / {selectedRow.summary.tenor}</span>
                        </div>
                        <div className="text-[11px] text-gray-600 font-mono-num">
                          {nextInst ? `Jatuh ${formatDate(nextInst.due_date)}` : "Lunas"}
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200 -m-px">
                      <div className="p-4 border border-gray-200 bg-emerald-50">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Sudah Dibayar</div>
                        <div className="font-mono-num font-bold text-xl mt-1 text-[#10B981]" data-testid="quick-info-paid">{formatIDR(selectedRow.summary.totalPaid)}</div>
                        <div className="text-[11px] text-gray-500 font-mono-num">
                          {(selectedRow.sale.installments || []).filter((i) => i.status === "paid").length} angsuran lunas
                        </div>
                      </div>
                      <div className="p-4 border border-gray-200 bg-red-50">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Angsuran Ini (Sisa)</div>
                        <div className="font-mono-num font-bold text-xl mt-1 text-[#E63946]" data-testid="quick-info-inst-sisa">{formatIDR(nextSisa)}</div>
                      </div>
                      <div className="p-4 border border-gray-200">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Sisa Cicilan (Disepakati)</div>
                        <div className="font-mono-num font-bold text-xl mt-1" data-testid="quick-info-total-sisa">{formatIDR(selectedRow.summary.remainingAll)}</div>
                        <div className="text-[11px] text-gray-500 font-mono-num">
                          {(selectedRow.sale.installments || []).filter((i) => i.status !== "paid").length} angsuran tersisa
                        </div>
                      </div>
                      <div className="p-4 border border-gray-200 bg-orange-50">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Nilai Pelunasan Hari Ini</div>
                        <div className="font-mono-num font-bold text-xl mt-1 text-[#F97316]" data-testid="quick-info-payoff">{formatIDR(selectedRow.summary.payoffToday)}</div>
                        <div className="text-[11px] text-emerald-600 font-mono-num">Hemat bunga {formatIDR(selectedRow.summary.savings)}</div>
                      </div>
                    </div>
                    <div className="md:col-span-3 text-[11px] text-gray-600 font-mono-num text-center py-1">
                      Rumus: <b>Pelunasan Hari Ini</b> ({formatIDR(selectedRow.summary.payoffToday)}) + <b>Hemat Bunga</b> ({formatIDR(selectedRow.summary.savings)}) = <b>Sisa Cicilan Disepakati</b> ({formatIDR(selectedRow.summary.remainingAll)})
                    </div>

                    <div className="md:col-span-3 flex flex-col md:flex-row gap-3 pt-1">
                      <Button
                        onClick={() => nextInst && openPay(selectedRow.sale, nextInst)}
                        disabled={!nextInst}
                        data-testid="quick-pay-inst-btn"
                        className="flex-1 h-11 rounded-none bg-[#2563EB] hover:bg-[#1d4ed8] text-white uppercase tracking-wider text-xs"
                      >
                        <Money size={14} className="mr-2" weight="bold" /> Bayar Angsuran #{nextInst?.sequence || "-"}
                      </Button>
                      <Button
                        onClick={() => openPay(selectedRow.sale, null, selectedRow.summary.payoffToday)}
                        data-testid="quick-payoff-btn"
                        className="flex-1 h-11 rounded-none bg-[#F97316] hover:bg-[#ea660c] text-white uppercase tracking-wider text-xs"
                      >
                        <Coins size={14} className="mr-2" weight="bold" /> Lunasi Sekarang · {formatIDR(selectedRow.summary.payoffToday)}
                      </Button>
                    </div>
                  </>
                )}

                {!selectedRow && (
                  <div className="md:col-span-3 text-center py-8 text-sm text-gray-500 border border-dashed border-gray-300">
                    Pilih no. rekening di atas untuk melihat detail mobil, angsuran berjalan, sisa cicilan, dan nilai pelunasan hari ini.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <SearchBar value={q} onChange={setQ} placeholder="Cari no. rekening / pelanggan / mobil..." testid="payments-search" count={grouped.length} />

        {/* DP Outstanding Panel */}
        {(() => {
          const dpRows = sales
            .map((s) => {
              const target = Number(s.dp_target || 0);
              const paid = Number(s.dp_paid_amount || 0);
              const schedule = (s.dp_schedule || []).filter((x) => x.status !== "paid").sort((a, b) => a.sequence - b.sequence);
              if (target <= 0 || paid >= target - 0.01 || schedule.length === 0) return null;
              return { sale: s, target, paid, remaining: target - paid, nextTermin: schedule[0], unpaidCount: schedule.length };
            })
            .filter(Boolean);
          if (dpRows.length === 0) return null;
          return (
            <div className="border border-gray-200 bg-white my-6 overflow-x-auto" data-testid="dp-outstanding-panel">
              <div className="p-5 border-b border-gray-200 bg-[#2563EB] text-white flex items-center gap-2">
                <div className="font-display font-bold uppercase tracking-wider text-sm">DP Belum Lunas ({dpRows.length})</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="p-3">No. Rek</th>
                    <th className="p-3">Pelanggan / Mobil</th>
                    <th className="p-3">Termin Berikutnya</th>
                    <th className="p-3 text-right">Sisa DP</th>
                    <th className="p-3 text-right">Total DP</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {dpRows.map((r) => (
                    <tr key={r.sale.id} className="border-b border-gray-100" data-testid={`dp-row-${r.sale.id}`}>
                      <td className="p-3 font-mono-num font-semibold text-xs">{r.sale.account_number || "-"}</td>
                      <td className="p-3 text-xs">
                        <div className="font-semibold">{r.sale.customer?.name}</div>
                        <div className="text-gray-500">{r.sale.car?.brand} {r.sale.car?.model}</div>
                      </td>
                      <td className="p-3 text-xs">
                        <div className="font-mono-num">#{r.nextTermin.sequence} · {formatDate(r.nextTermin.due_date)}</div>
                        <div className="text-gray-500">{r.unpaidCount} termin tersisa</div>
                      </td>
                      <td className="p-3 text-right font-mono-num font-bold text-[#E63946]">{formatIDR(r.remaining)}</td>
                      <td className="p-3 text-right font-mono-num text-xs text-gray-600">{formatIDR(r.target)}</td>
                      <td className="p-3 text-right">
                        <Button onClick={() => openDpPay(r.sale, r.nextTermin)} data-testid={`dp-pay-btn-${r.sale.id}`} className="h-8 rounded-none bg-[#2563EB] hover:bg-[#1d4ed8] text-white uppercase tracking-wider text-[10px]">
                          Bayar DP #{r.nextTermin.sequence}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        <div className="space-y-6">
          {grouped.map(({ sale, summary }) => {
            const unpaid = (sale.installments || []).filter((i) => i.status !== "paid").sort((a, b) => a.sequence - b.sequence);
            return (
              <div key={sale.id} className="border border-gray-200" data-testid={`sale-card-${sale.id}`}>
                {/* Header */}
                <div className="p-5 border-b border-gray-200 bg-gray-50 flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="font-mono-num font-bold text-lg">{sale.account_number}</div>
                      <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wider bg-gray-900 text-white">Kredit</span>
                    </div>
                    <div className="mt-1">
                      <span className="font-semibold">{sale.customer?.name}</span>
                      <span className="text-gray-500 text-sm"> · {sale.customer?.phone}</span>
                    </div>
                    <div className="text-sm text-gray-600">{sale.car?.brand} {sale.car?.model} {sale.car?.type ? "· " + sale.car.type : ""}</div>
                    <div className="text-xs text-gray-500 mt-1 font-mono-num">
                      Sejak {formatDate(sale.sale_date)} · Bunga {summary.rate}%/thn · Tenor {summary.tenor} bln · Berjalan ~{summary.elapsed.toFixed(1)} bln
                    </div>
                  </div>
                  <Link to={`/sales/${sale.id}`} className="text-xs uppercase tracking-wider text-gray-600 hover:text-black inline-flex items-center gap-1">
                    Detail <ArrowRight size={12} />
                  </Link>
                </div>

                {/* KPI */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border-b border-gray-200">
                  <div className="p-5 border-r border-gray-200 bg-emerald-50">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                      <CheckCircle size={12} weight="bold" /> Sudah Dibayar
                    </div>
                    <div className="font-mono-num text-lg font-bold mt-2 text-[#10B981]" data-testid={`paid-${sale.id}`}>{formatIDR(summary.totalPaid)}</div>
                    <div className="text-[11px] text-gray-500 font-mono-num">
                      {(sale.installments || []).filter((i) => i.status === "paid").length} angsuran lunas
                    </div>
                  </div>
                  <div className={`p-5 border-r border-gray-200 ${summary.overdueCount > 0 ? "bg-red-50" : ""}`}>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                      <Warning size={12} weight="bold" /> Total Tunggakan
                    </div>
                    <div className="font-mono-num text-lg font-bold mt-2" style={{ color: summary.overdueCount > 0 ? "#E63946" : "#0A0A0A" }} data-testid={`overdue-${sale.id}`}>
                      {formatIDR(summary.overdueAmount)}
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono-num">{summary.overdueCount} angsuran telat</div>
                  </div>
                  <div className="p-5 border-r border-gray-200">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                      <Wallet size={12} weight="bold" /> Sisa Cicilan (Disepakati)
                    </div>
                    <div className="font-mono-num text-lg font-bold mt-2" data-testid={`remaining-${sale.id}`}>{formatIDR(summary.remainingAll)}</div>
                    <div className="text-[11px] text-gray-500 font-mono-num">{unpaid.length} angsuran</div>
                  </div>
                  <div className="p-5 border-r border-gray-200 bg-orange-50">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                      <Coins size={12} weight="bold" /> Pelunasan Hari Ini
                    </div>
                    <div className="font-mono-num text-lg font-bold mt-2 text-[#F97316]" data-testid={`payoff-${sale.id}`}>{formatIDR(summary.payoffToday)}</div>
                    <div className="text-[11px] text-gray-600 font-mono-num">Bunga jadi {(summary.rate * summary.elapsed / summary.tenor || 0).toFixed(1)}%</div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                      <CheckCircle size={12} weight="bold" /> Hemat Bunga
                    </div>
                    <div className="font-mono-num text-lg font-bold mt-2 text-[#10B981]" data-testid={`savings-${sale.id}`}>{formatIDR(summary.savings)}</div>
                    <Button
                      onClick={() => openPay(sale, null, summary.payoffToday)}
                      data-testid={`payoff-btn-${sale.id}`}
                      className="mt-2 h-8 w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-[10px]"
                    >
                      Lunasi Sekarang
                    </Button>
                  </div>
                </div>
                <div className="px-5 py-2 border-b border-gray-200 bg-gray-50 text-[11px] text-gray-600 font-mono-num text-center">
                  Pelunasan ({formatIDR(summary.payoffToday)}) + Hemat Bunga ({formatIDR(summary.savings)}) = Sisa Cicilan Disepakati ({formatIDR(summary.remainingAll)})
                </div>

                {/* Installments list */}
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-white border-b border-gray-200">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                      <th className="p-3 pl-5">Angs. #</th>
                      <th className="p-3">Jatuh Tempo</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Jumlah</th>
                      <th className="p-3 text-right">Sisa</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaid.map((inst) => {
                      const due = new Date(inst.due_date + "T00:00:00");
                      const days = Math.floor((today - due) / (1000 * 60 * 60 * 24));
                      const b = daysBadge(days);
                      const sisa = Number(inst.amount) - Number(inst.paid_amount || 0);
                      return (
                        <tr key={inst.id} className="border-b border-gray-50" data-testid={`inst-${inst.id}`}>
                          <td className="p-3 pl-5 font-mono-num">#{inst.sequence}</td>
                          <td className="p-3 font-mono-num text-xs">{formatDate(inst.due_date)}</td>
                          <td className="p-3">
                            <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wider text-white font-semibold" style={{ backgroundColor: b.bg }}>
                              {b.label}
                            </span>
                          </td>
                          <td className="p-3 text-right font-mono-num">{formatIDR(inst.amount)}</td>
                          <td className="p-3 text-right font-mono-num font-semibold">{formatIDR(sisa)}</td>
                          <td className="p-3 text-right">
                            <Button onClick={() => openPay(sale, inst)} data-testid={`pay-${inst.id}`} className="h-7 rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-[10px]">
                              <Money size={11} className="mr-1" /> Bayar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            );
          })}
          {grouped.length === 0 && (
            <div className="border border-gray-200 p-12 text-center text-sm text-gray-500">
              {sales.length === 0 ? "Belum ada transaksi kredit" : "Semua cicilan sudah lunas 🎉"}
            </div>
          )}
        </div>
      </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none no-print">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{selected?.isDp ? `Bayar DP Termin #${selected?.dpTermin?.sequence}` : selected?.isPayoff ? "Pelunasan Rekening" : "Catat Pembayaran"}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="border border-gray-200 p-4 space-y-1">
                <div className="text-sm"><span className="text-gray-500">Rekening:</span> <span className="font-mono-num font-semibold ml-2">{selected.sale.account_number}</span></div>
                <div className="text-sm"><span className="text-gray-500">Pelanggan:</span> <span className="font-semibold ml-2">{selected.sale.customer?.name}</span></div>
                <div className="text-sm"><span className="text-gray-500">Mobil:</span> <span className="ml-2">{selected.sale.car?.brand} {selected.sale.car?.model}</span></div>
                {!selected.isPayoff && !selected.isDp && <div className="text-sm"><span className="text-gray-500">Angsuran #{selected.inst.sequence}</span> · <span className="font-mono-num">{formatDate(selected.inst.due_date)}</span></div>}
                {selected.isDp && <div className="text-sm text-[#2563EB]">Termin DP #{selected.dpTermin.sequence} · <span className="font-mono-num">{formatDate(selected.dpTermin.due_date)}</span></div>}
                {selected.isPayoff && <div className="text-sm text-[#F97316] font-mono-num">Melunasi semua angsuran tersisa</div>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Jumlah Bayar</Label>
                <RupiahInput value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-2 rounded-none" data-testid="quick-pay-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tanggal</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-2 rounded-none" data-testid="quick-pay-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Kas Tujuan</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPayDestination("SM")}
                    data-testid="dest-sm"
                    className={`h-11 border transition-colors uppercase tracking-wider text-xs font-semibold ${payDestination === "SM" ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "border-gray-300 hover:border-gray-900"}`}
                  >
                    Kas Showroom (SM)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayDestination("KP")}
                    data-testid="dest-kp"
                    className={`h-11 border transition-colors uppercase tracking-wider text-xs font-semibold ${payDestination === "KP" ? "bg-[#F97316] border-[#F97316] text-white" : "border-gray-300 hover:border-gray-900"}`}
                  >
                    Kas Kantor Pusat (KP)
                  </button>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Default: Showroom (SM). Pilih KP jika uang langsung disetor ke kantor pusat.</div>
              </div>
              <Button onClick={submitPay} data-testid="quick-pay-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">
                Simpan Pembayaran
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Printable Payoff Receipt */}
      {settings && payoffReceipt && (
        <PayoffReceipt
          sale={payoffReceipt.sale}
          settings={settings}
          amount={payoffReceipt.amount}
          date={payoffReceipt.date}
          onDone={() => setPayoffReceipt(null)}
        />
      )}
    </div>
  );
}
